import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Coins } from 'lucide-react';
import { useAiUsage, type UsageDays } from '@/api/ai';
import { Card, ErrorState, Segmented, Skeleton } from '@/components/ui';
import { AI_FEATURE_LABEL } from '@/features/platform/meta';
import { cn } from '@/lib/cn';
import { formatNumber, formatTokens, humanize } from '@/lib/format';

const RANGES: { value: UsageDays; label: string }[] = [
  { value: 1, label: '24h' },
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
];

const tokens = (r: { inputTokens: number; outputTokens: number }) => r.inputTokens + r.outputTokens;
const inOut = (r: { inputTokens: number; outputTokens: number }) =>
  `${formatNumber(r.inputTokens)} in · ${formatNumber(r.outputTokens)} out`;

/** Token spend for the workspace: totals, per model, per feature and the tickets that used the most. */
export function AiUsageCard({
  workspaceId,
  className = 'mt-8',
}: {
  workspaceId?: string;
  className?: string;
}) {
  const [days, setDays] = useState<UsageDays>(30);
  const usage = useAiUsage(days, workspaceId);

  return (
    <Card className={cn('p-5', className)}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600 ring-1 ring-amber-100">
            <Coins className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <h2 className="text-base font-bold">Token usage</h2>
            <p className="text-sm text-slate-500">
              Tokens billed by your AI providers — input (prompt) and output (reply).
            </p>
          </div>
        </div>
        <Segmented label="Usage period" size="sm" value={days} options={RANGES} onChange={setDays} />
      </div>

      {usage.isPending ? (
        <Skeleton className="h-48 rounded-xl" />
      ) : usage.error ? (
        <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />
      ) : usage.data.totals.requests === 0 ? (
        <p className="py-8 text-center text-sm text-slate-400">
          No AI requests in this period. Usage appears here as the AI answers chats, drafts replies and
          summarises tickets.
        </p>
      ) : (
        <>
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat
              label="Total tokens"
              value={formatTokens(tokens(usage.data.totals))}
              hint={inOut(usage.data.totals)}
            />
            <Stat label="Input tokens" value={formatNumber(usage.data.totals.inputTokens)} />
            <Stat label="Output tokens" value={formatNumber(usage.data.totals.outputTokens)} />
            <Stat
              label="AI requests"
              value={formatNumber(usage.data.totals.requests)}
              hint={usage.data.totals.failed ? `${usage.data.totals.failed} failed` : undefined}
            />
          </dl>

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <section>
              <h3 className="mb-2 text-sm font-semibold text-slate-900">Tickets using the most tokens</h3>
              {usage.data.topTickets.length === 0 ? (
                <p className="text-sm text-slate-400">No ticket-linked usage yet</p>
              ) : (
                <ul className="divide-y divide-slate-100 text-sm">
                  {usage.data.topTickets.map((t) => (
                    <li key={t.ticketId} className="flex items-center justify-between gap-3 py-2">
                      {workspaceId ? (
                        // Platform console: the ticket lives in another workspace, so no inbox link.
                        <span className="min-w-0 truncate text-slate-800">
                          <span className="text-slate-400">#{t.number ?? '—'}</span>{' '}
                          {t.subject ?? 'Deleted ticket'}
                        </span>
                      ) : (
                        <Link
                          to={`/inbox/${t.ticketId}`}
                          className="hover:text-brand-700 min-w-0 truncate text-slate-800"
                        >
                          <span className="text-slate-400">#{t.number ?? '—'}</span>{' '}
                          {t.subject ?? 'Deleted ticket'}
                        </Link>
                      )}
                      <span className="shrink-0 text-right tabular-nums" title={inOut(t)}>
                        <span className="block font-semibold text-slate-900">{formatTokens(tokens(t))}</span>
                        <span className="text-xs text-slate-500">
                          {t.requests} {t.requests === 1 ? 'request' : 'requests'}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="space-y-5">
              <div>
                <h3 className="mb-2 text-sm font-semibold text-slate-900">By feature</h3>
                <UsageRows
                  rows={usage.data.byFeature.map((f) => ({
                    key: f.feature,
                    label: AI_FEATURE_LABEL[f.feature] ?? humanize(f.feature),
                    ...f,
                  }))}
                />
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold text-slate-900">By model</h3>
                <UsageRows
                  rows={usage.data.byModel.map((m) => ({
                    key: `${m.provider}/${m.model}`,
                    label: m.model,
                    ...m,
                  }))}
                  mono
                />
              </div>
            </section>
          </div>
        </>
      )}
    </Card>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl bg-slate-50 p-3 ring-1 ring-slate-100">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-lg font-bold text-slate-900 tabular-nums">{value}</dd>
      {hint && <dd className="text-[11px] text-slate-500 tabular-nums">{hint}</dd>}
    </div>
  );
}

function UsageRows({
  rows,
  mono = false,
}: {
  rows: { key: string; label: string; requests: number; inputTokens: number; outputTokens: number }[];
  mono?: boolean;
}) {
  const max = Math.max(1, ...rows.map(tokens));
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => (
        <li key={r.key} title={inOut(r)}>
          <div className="mb-1 flex justify-between gap-3 text-xs">
            <span className={mono ? 'truncate font-mono text-slate-700' : 'truncate text-slate-700'}>
              {r.label}
            </span>
            <span className="shrink-0 text-slate-500 tabular-nums">
              <span className="font-semibold text-slate-900">{formatTokens(tokens(r))}</span> · {r.requests}{' '}
              req
            </span>
          </div>
          <div className="h-2 rounded-full bg-slate-100">
            <div
              className="h-2 rounded-full bg-amber-400"
              style={{ width: `${Math.max(2, (tokens(r) / max) * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
